import React from 'react';
import { Box, Tab, Tabs, useTheme, useMediaQuery } from "@mui/material";
import { grey } from '@mui/material/colors';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import Person3OutlinedIcon from '@mui/icons-material/Person3Outlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';

import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import { Link, useLocation, Routes, Route, Navigate } from 'react-router-dom';

// Componentes
import UsuariosComponent from '../components/users/Usuarios';
import RolesComponent from '../components/roles/Roles';
import ClientesComponent from '../components/clientes/Clientespage';
import ReportesComponent from '../components/Reportes/reportesPage';
import EquipoComponent from '../components/equipos/Equipo';
import TareasComponent from '../components/tareas/Tareaspage';
import AgendaComponent from '../components/agenda/agendaPage';

const tabStyles = {
  minHeight: '40px',
  borderRadius: '40px',
  color: 'black',
  transition: '0.3s',
  whiteSpace: 'nowrap',
  '&.Mui-selected': {
    bgcolor: 'white', 
    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
    color: 'black',
  },
  '&:hover': {
    bgcolor: grey[200],
    color: 'black',
  }
};

export default function MainMenu() {
  const location = useLocation();
  const theme = useTheme();
  const isSmallDesktop = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const rawData = localStorage.getItem('user');

    if (!rawData) {
    return <Navigate to="/home" replace />;
  }

  // --- SECCIÓN DE DEPURACIÓN (LOGS) ---
  const user = JSON.parse(rawData);
  
  // IMPORTANTE: 'permisos' debe coincidir con el nombre que definimos en el Backend
  const userPermissions = user?.permisos || [];

  // 2. Configuración de pestañas
  const allTabs = [
    { label: "Usuarios", path: "/home/menu/usuarios", icon: <Person3OutlinedIcon/>, permission: "Gestionar Usuarios", component: <UsuariosComponent/> },
    { label: "Roles", path: "/home/menu/roles", icon: <ShieldOutlinedIcon/>, permission: "Gestionar Roles", component: <RolesComponent/> },
    { label: "Clientes", path: "/home/menu/clientes", icon: <PeopleOutlineOutlinedIcon/>, permission: "Gestionar Clientes", component: <ClientesComponent/> },
    { label: "Equipo", path: "/home/menu/equipo", icon: <EmojiObjectsOutlinedIcon/>, permission: "Gestionar equipos/soluciones", component: <EquipoComponent/> },
    { label: "Tareas", path: "/home/menu/tareas", icon: <TaskOutlinedIcon/>, permission: "Gestionar Tareas", component: <TareasComponent/> },
    // Agenda y Reportes: basta con el permiso de "Ver" para acceder
    // (en modo solo lectura). Gestionar habilita además crear/editar/eliminar.
    { label: "Agenda", path: "/home/menu/gestion-agenda", icon: <CalendarMonthOutlinedIcon/>, anyOf: ["Gestionar Agenda", "Ver Agenda"], component: <AgendaComponent/> },
    { label: "Reportes", path: "/home/menu/reportes", icon: <AssessmentOutlinedIcon/>, anyOf: ["Gestionar reportes", "Ver Reportes"], component: <ReportesComponent/> },
  ];

  // 3. Filtrado de pestañas permitidas
  const allowedTabs = allTabs.filter(tab => {
    if (tab.anyOf) return tab.anyOf.some(p => userPermissions.includes(p));
    return tab.permission === null || userPermissions.includes(tab.permission);
  });

  const currentTabIndex = allowedTabs.findIndex(tab => tab.path === location.pathname);
  const activeValue = currentTabIndex === -1 ? 0 : currentTabIndex;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/home';
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection:'column' }}>
      <Box 
        sx={{ 
          m:2,
          bgcolor: grey[300], 
          borderRadius: '40px', 
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeValue}
          variant={isSmallDesktop ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            px: 2, 
            minHeight: '50px',
            '& .MuiTabs-flexContainer': {
              gap: isMobile ? '5px' : '10px', 
              height: '100%',
              alignItems: 'center',
            },
            '& .MuiTab-root': {
              flex: isSmallDesktop ? '0 0 auto' : 1, 
              maxWidth: 'none',
              textTransform: 'none', 
              px:2,
              minWidth: { xs: '50px', sm: 'auto' }
            },
            '& .MuiTabs-indicator': { display:'none' },
          }}
        >
          {allowedTabs.map((tab) => (
            <Tab 
              key={tab.path}
              component={Link} 
              to={tab.path} 
              icon={tab.icon} 
              iconPosition="start" 
              label={tab.label} 
              sx={tabStyles} 
            />
          ))}

          <Tab 
            onClick={handleLogout} 
            icon={<ExitToAppOutlinedIcon/>} 
            iconPosition="start" 
            label="Cerrar sesión" 
            sx={tabStyles} 
          />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        <Routes>
          {/* Redirigir a la primera pestaña permitida por defecto */}
          <Route path="/" element={<Navigate to={allowedTabs[0]?.path || "Ver agenda"} replace />} />
          
          {/* Generar rutas dinámicamente según permisos */}
          {allowedTabs.map((tab) => (
            <Route 
              key={tab.path} 
              path={tab.path.split('/').pop()} 
              element={tab.component} 
            />
          ))}

          {/* Fallback si intenta entrar a algo no permitido */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}